import { EVENT_DELAY } from '@src/config/constants'
import { getCartIDAndAuth } from '@src/quince-site-common/src/hooks/cart/hooks/new-cart-helper'
import { ANALYTIC_COOKIES } from '@src/quince-site-common/src/utils/constants'
import getCookie from '@src/quince-site-common/src/utils/getCookie'
import { getlbUserId } from '@src/quince-site-common/src/utils/getlb-user-id'
import isBrowser from '@src/quince-site-common/src/utils/isBrowser'
import { executeFireAndForgotTask } from '@src/utils/fire-and-forgot-task'
import { getLocalisationDataFromStorage } from '@src/utils/general.utils'

import { TELEMETRY_DOMAINS } from '../telemetry-domains.constants'
import { TELEMETRY_EVENT_NAMES } from '../telemetry-events.constants'
import { TelemetryIntegration } from '../telemetry-events.types'

import {
  formatRevenue,
  formatValue,
  getSHA256Hashed,
  processImageUrl,
  removeHyphensFromEventId,
} from './provider-utils/data-layer-pixel-utils'

export const DatalayerPixel: TelemetryIntegration = {
  config: {
    enabled: true,
    eventIdealTimeout: 1000,
    domain: TELEMETRY_DOMAINS.GOOGLE_TAG_MANAGER,
  },
  events: {
    [TELEMETRY_EVENT_NAMES.ADD_TO_CART]: async (properties: any) => {
      const { cartID, authCode } = getCartIDAndAuth()
      const hashedEmail = await getSHA256Hashed(
        getCookie(ANALYTIC_COOKIES.LB_CUSTOMER_EMAIL) ?? getCookie(ANALYTIC_COOKIES.LB_CUSTOMER_TENTATIVE_EMAIL),
      )
      const eventId = properties?.eventId?.toString() ?? ''
      const alphanumericEventId = removeHyphensFromEventId(eventId)
      const localisationData = getLocalisationDataFromStorage()
      logDatalayerPixelEvent('add_to_cart', 'dl_add_to_cart_properties', {
        variantId: properties?.variantId,
        productTitle: properties?.productTitle,
        variantTitle: properties?.variantTitle,
        price: formatRevenue(properties?.price),
        category: properties?.productType,
        quantity: properties?.quantity,
        value: formatValue(properties?.price, properties?.quantity),
        currency: localisationData?.currency,
        country: localisationData?.country,
        language: localisationData?.language,
        productId: properties?.productId,
        variantUrl: properties?.variantImageUrl && processImageUrl(properties?.variantImageUrl),
        department: properties?.department,
        division: properties?.division,
        subDepartment: properties?.subDepartment,
        class: properties?.class,
        subClass: properties?.subClass,
        sku: properties?.sku,
        brand: properties?.vendor,
        cartId: cartID,
        checkoutId: cartID,
        checkoutAuthCode: authCode,
        itemName: `${properties?.productTitle?.toString() ?? ''} - ${properties?.variantTitle?.toString() ?? ''}`,
        internalVariantId: properties?.internal_variant_id,
        internalProductId: properties?.internal_product_id,
        email: hashedEmail,
        lbUserId: getlbUserId()?.trim() ?? '',
        zipcode: getCookie(ANALYTIC_COOKIES.USER_ENTERED_ZIP_CODE) ?? getCookie(ANALYTIC_COOKIES.ZIP_CODE),
        eventId,
        alphanumericEventId,
        eventTimestamp: Date.now(),
      })
    },
    [TELEMETRY_EVENT_NAMES.PRODUCT_VIEW]: async (properties: any) => {
      const hashedEmail = await getSHA256Hashed(
        getCookie(ANALYTIC_COOKIES.LB_CUSTOMER_EMAIL) ?? getCookie(ANALYTIC_COOKIES.LB_CUSTOMER_TENTATIVE_EMAIL),
      )
      const eventId = properties?.eventId?.toString() ?? ''
      const alphanumericEventId = removeHyphensFromEventId(eventId)
      const localisationData = getLocalisationDataFromStorage()
      logDatalayerPixelEvent('product_view', 'dl_product_view_properties', {
        variantId: properties?.variantId,
        productTitle: properties?.productTitle,
        variantTitle: properties?.variantTitle,
        price: formatRevenue(properties?.price),
        category: properties?.productType,
        currency: localisationData?.currency,
        country: localisationData?.country,
        language: localisationData?.language,
        productId: properties?.productId,
        variantUrl: properties?.variantImageUrl && processImageUrl(properties?.variantImageUrl),
        department: properties?.department,
        division: properties?.division,
        subDepartment: properties?.subDepartment,
        class: properties?.class,
        subClass: properties?.subClass,
        sku: properties?.sku,
        brand: properties?.vendor,
        itemName: `${properties?.productTitle?.toString() ?? ''} - ${properties?.variantTitle?.toString() ?? ''}`,
        internalVariantId: properties?.internal_variant_id,
        internalProductId: properties?.internal_product_id,
        email: hashedEmail,
        lbUserId: getlbUserId()?.trim() ?? '',
        zipcode: getCookie(ANALYTIC_COOKIES.USER_ENTERED_ZIP_CODE) ?? getCookie(ANALYTIC_COOKIES.ZIP_CODE),
        eventId,
        alphanumericEventId,
        eventTimestamp: Date.now(),
      })
    },
  },
}
export const logDatalayerPixelEvent = (eventName: string, dataLayerKey: string, properties: any) => {
  executeFireAndForgotTask(() => {
    try {
      if (isBrowser() && eventName && dataLayerKey) {
        window.dataLayer = window.dataLayer ?? []
        window.dataLayer.push({
          event: `dl_${eventName}`,
          [dataLayerKey]: { ...properties },
        })
      }
    } catch (error) {
      console.error('Error logging DataLayer Pixel Event:', error)
    }
  }, EVENT_DELAY)
}
